// ─── Shared-in text (WhatsApp and anything else) ─────────────
// WhatsApp exposes no API for reading personal or group threads, so the way a
// message from a group gets into the planner is the owner sharing it out of
// WhatsApp. Three routes all land here:
//
//   • Android: the installed PWA registers a `share_target` (see
//     public/manifest.json), so "Share → NB Info" from a WhatsApp thread opens
//     the planner with the message text attached.
//   • iOS: share targets are not supported for web apps, but a two-step
//     Shortcut ("Receive text from share sheet" → "Open URL") does the same
//     thing by opening this page with ?share=<text>.
//   • Anywhere: paste into the Inbox box by hand.
//
// Whatever the route, the text is parsed into a *candidate* and waits for an
// explicit accept — sharing a message never books anything on its own.

const SHARE_PARAMS = ['share', 'text', 'body'] as const

export interface SharedPayload {
  text: string
  /** The share sheet's title/subject, when the source app supplies one */
  title?: string
  /** A URL shared alongside the text, appended so the parser can see it */
  url?: string
}

/**
 * Reads a shared message off the current URL. Does not modify the URL — call
 * `clearSharedText()` once the payload has been consumed, so a reload doesn't
 * re-add the same message.
 */
export function readSharedText(): SharedPayload | null {
  try {
    const params = new URLSearchParams(window.location.search)
    const parts: string[] = []
    for (const key of SHARE_PARAMS) {
      const value = params.get(key)
      if (value) parts.push(value)
    }
    const url = params.get('url') || undefined
    const title = params.get('title') || undefined
    if (parts.length === 0 && !title) return null

    // A shared URL on its own is not an event; keep it as trailing context.
    const text = [...parts, url].filter(Boolean).join('\n').trim()
    if (!text && !title) return null
    return { text: text || title!, title, url }
  } catch {
    return null
  }
}

export function clearSharedText(): void {
  try {
    const params = new URLSearchParams(window.location.search)
    ;[...SHARE_PARAMS, 'url', 'title'].forEach((key) => params.delete(key))
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    )
  } catch {
    // history unavailable — harmless, the param is only read once per mount
  }
}

/**
 * WhatsApp's own export format prefixes every line with a timestamp and sender:
 *   `[8/17/26, 6:31:02 PM] Dana R.: End of season party Friday 6:30pm`
 * Strip that so the date parser sees the message rather than the metadata, and
 * report the sender separately.
 */
export function stripWhatsAppTranscript(raw: string): { text: string; from?: string } {
  const lines = raw.split('\n').filter((l) => l.trim())
  const pattern = /^\[?(\d{1,2}\/\d{1,2}\/\d{2,4}),?\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:[AaPp]\.?[Mm]\.?)?\]?\s*[-–]?\s*([^:]{1,60}):\s*(.*)$/
  const senders: string[] = []
  const body: string[] = []
  let matched = false

  for (const line of lines) {
    const match = line.match(pattern)
    if (match) {
      matched = true
      senders.push(match[2].trim())
      body.push(match[3].trim())
    } else {
      body.push(line.trim())
    }
  }
  if (!matched) return { text: raw.trim() }
  return { text: body.filter(Boolean).join('\n'), from: senders[0] }
}
